import * as S from './ExpressionView.styles';

import * as _ from 'lodash';
import * as React from 'react';
import makeComp from '../../util/profiler';
import * as CG from './cg/graph';
import * as HL from './cg/hl';
import * as Code from './cg/code';

const ArgsView: React.FC<{
  args: HL.EditingOpInputs;
}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  ({args}) => {
    const argNames = Object.keys(args);
    const argValues = Object.values(args);
    return (
      <>
        (
        {argValues.map((inNode, i) => (
          <span key={i}>
            {argNames[i]}: {<NodeView node={inNode} />}
            {i < argNames.length - 1 && (
              <span style={{pointerEvents: 'none'}}>,&nbsp;</span>
            )}
          </span>
        ))}
        )
      </>
    );
  },
  {id: 'ArgView'}
);

const OpView: React.FC<{op: HL.EditingOp}> = makeComp(
  // TODO: What to do if inputType is no longer valid for a step?
  ({op}) => {
    const argNames = Object.keys(op.inputs);
    const argValues = Object.values(op.inputs);
    if (HL.isBinaryOp(op)) {
      // Binary op
      return (
        <span>
          <NodeView node={argValues[0]} /> {HL.binaryOpSymbol(op)}{' '}
          <NodeView node={argValues[1]} />
        </span>
      );
    }
    if (HL.isChainedUnaryOp(op)) {
      // Render "pick" as index notation
      if (op.name === 'pick') {
        // If we're picking directly of a variable and we the key is a const,
        // just render the key itself with no quotes
        const obj = argValues[0];
        const key = argValues[1];
        if (obj.nodeType === 'var' && key.nodeType === 'const') {
          return <span>{key.val}</span>;
        }

        // Otherwise [] index notation
        return (
          <span>
            <NodeView node={argValues[0]} />
            [
            <NodeView node={argValues[1]} />]
          </span>
        );
      }
      return (
        <span>
          <NodeView node={argValues[0]} />.{HL.opDisplayName(op)}
          <ArgsView args={_.pickBy(op.inputs, (v, k) => k !== argNames[0])} />
        </span>
      );
    }
    // Render as function call
    return (
      <span>
        {HL.opDisplayName(op)}
        <ArgsView args={op.inputs} />
      </span>
    );
  },
  {id: 'OpView'}
);

const OutputNodeView: React.FC<{
  node: HL.EditingOutputNode;
}> = makeComp(
  ({node}) => {
    return <OpView op={node.fromOp} />;
  },
  {id: 'OutputNodeView'}
);

const ConstNodeView: React.FC<{
  node: CG.ConstNode;
}> = makeComp(
  ({node}) => {
    return CG.constNodeIsType(node, 'string') ? (
      <span>{`"${node.val}"`}</span>
    ) : CG.constNodeIsType(node, 'number') ? (
      <span>{node.val.toString()}</span>
    ) : (
      <div>?</div>
    );
  },
  {id: 'ConstNodeView'}
);

export const NodeView: React.FC<{
  node: HL.EditingNode;
}> = makeComp(
  ({node}) => {
    return (
      <S.ExpressionView>
        {node.nodeType === 'var' ? (
          node.varName
        ) : node.nodeType === 'output' ? (
          <OutputNodeView node={node} />
        ) : node.nodeType === 'const' ? (
          <ConstNodeView node={node} />
        ) : (
          <></>
        )}
      </S.ExpressionView>
    );
  },
  {id: 'NodeView'}
);

export const ExpressionView: React.FC<{
  frame?: Code.Frame;
  node: HL.EditingNode;
}> = makeComp(
  ({node}) => {
    return <NodeView node={node} />;
  },
  {id: 'ExpressionViewContainer'}
);

// These functions should produce the same result as ExpressionView

export const simpleArgsString = (args: HL.EditingOpInputs): string =>
  '(' +
  Object.values(args)
    .map(v => simpleNodeString(v))
    .join(', ') +
  ')';

export const simpleOpString = (op: HL.EditingOp): string => {
  const argNames = Object.keys(op.inputs);
  const argValues = Object.values(op.inputs);
  if (HL.isBinaryOp(op)) {
    return `${simpleNodeString(argValues[0])} ${HL.binaryOpSymbol(
      op
    )} ${simpleNodeString(argValues[1])}`;
  }
  if (HL.isChainedUnaryOp(op)) {
    if (op.name === 'pick') {
      const obj = argValues[0];
      const key = argValues[1];
      if (obj.nodeType === 'var' && key.nodeType === 'const') {
        return key.val;
      }
      return `${simpleNodeString(obj)}[${simpleNodeString(key)}]`;
    }
    return `${simpleNodeString(argValues[0])}.${HL.opDisplayName(
      op
    )}${simpleArgsString(_.pickBy(op.inputs, (v, k) => k !== argNames[0]))}`;
  }
  return `${HL.opDisplayName(op)} ${simpleArgsString(op.inputs)} `;
};

export const simpleNodeString = (node: HL.EditingNode): string =>
  node.nodeType === 'var'
    ? node.varName
    : node.nodeType === 'output'
    ? simpleOpString(node.fromOp)
    : node.nodeType === 'const'
    ? node.type === 'number'
      ? node.val.toString()
      : node.type === 'string'
      ? node.val
      : '?'
    : '-';
